
import { Vue, Component } from 'vue-property-decorator';
import mwx from '@/store/modules/mediumwarex';
import { auth } from '@/store/firestore';

const SERVERS = [
	{ label: 'Production', url: 'https://api-sqlacc-dashboard.sql.com.my' },
	{ label: 'Inhouse', url: 'https://api.gobi.my' },
];

@Component
export default class ServerMonitor extends Vue {
	public selectedServer: number = 0;
	public debugToken: string = '';
	public refreshInterval: number = 0;
	public loading: boolean = false;
	public refreshTimer: number | null = null;

	// Jobs
	public showAccountName: boolean = false;
	public durationMode: number = 0;
	public durationModes = [
		{ key: 'total', label: 'Duration' },
		{ key: 'queue', label: 'Queued' },
		{ key: 'extract', label: 'Extract' },
		{ key: 'upload', label: 'Upload' },
		{ key: 'parquetConvert', label: 'Pqt Convert' },
		{ key: 'parquetUpload', label: 'Pqt Upload' },
		{ key: 'firestore', label: 'Firestore' },
	];
	public jobSummary: any = null;
	public jobsList: any[] = [];
	public jobsError: string = '';

	// Files
	public filesData: any = null;
	public filesError: string = '';
	public showUploads: boolean = true;
	public showTempCsv: boolean = true;

	// Pools
	public poolStats: any = null;

	// Active file map (tempCsv filename -> activity flags)
	public activeFileMap: Map<string, any> = new Map();

	// System
	public systemData: any = null;

	// Logs
	public logs: string = '';
	public logsError: string = '';

	// Parquet Config
	public parquetAllowAll: boolean = false;
	public parquetAccountsInput: string = '';
	public parquetBudgetMB: number = 150;
	public maxExtracts: number = 10;
	public maxUploads: number = 20;
	public parquetConfigError: string = '';

	public jobFields = [
		{ key: 'jobId', label: 'Job ID' },
		{ key: 'accountId', label: 'Account' },
		{ key: 'group', label: 'Group' },
		{ key: 'status', label: 'Status' },
		{ key: 'csvUp', label: 'CSV Up' },
		{ key: 'convert', label: 'Convert' },
		{ key: 'pqtUp', label: 'Pqt Up' },
		{ key: 'createdAt', label: 'Age' },
		{ key: 'durations', label: 'Durations' },
		{ key: 'error', label: 'Error' },
		{ key: 'actions', label: '' },
	];

	public fileFields = [
		{ key: 'name', label: 'Name' },
		{ key: 'sizeMB', label: 'Size' },
		{ key: 'ageMinutes', label: 'Age' },
		{ key: 'protected', label: '' },
	];

	public tempCsvFields = [
		{ key: 'name', label: 'Name' },
		{ key: 'sizeMB', label: 'Size' },
		{ key: 'ageMinutes', label: 'Age' },
		{ key: 'activity', label: 'Activity' },
		{ key: 'protected', label: '' },
	];

	public get poolList(): any[] {
		if (!this.poolStats) { return []; }
		return [
			{ name: 'Extract', ...this.poolStats.extract },
			{ name: 'Upload', ...this.poolStats.upload },
			{ name: 'Parquet', ...this.poolStats.parquet },
		];
	}

	public get baseUrl(): string {
		return SERVERS[this.selectedServer].url;
	}

	public mounted() {
		this.debugToken = localStorage.getItem('monitor_debug_token') || '';
		if (this.debugToken || auth.currentUser) {
			this.refreshAll();
		}
	}

	public beforeDestroy() {
		this.clearTimer();
	}

	public saveToken() {
		localStorage.setItem('monitor_debug_token', this.debugToken);
	}

	public onServerChange() {
		if (this.debugToken) {
			this.refreshAll();
		}
	}

	public onRefreshIntervalChange() {
		this.clearTimer();
		if (this.refreshInterval > 0) {
			this.refreshTimer = window.setInterval(() => {
				this.refreshAll();
			}, this.refreshInterval * 1000);
		}
	}

	public clearTimer() {
		if (this.refreshTimer !== null) {
			clearInterval(this.refreshTimer);
			this.refreshTimer = null;
		}
	}

	public async getAuthToken(): Promise<string> {
		if (this.debugToken) { return this.debugToken; }
		const user = auth.currentUser;
		if (user) { return user.getIdToken(); }
		throw new Error('No debug token and no Firebase user signed in');
	}

	public async apiFetch(path: string, method: string = 'GET', body?: any): Promise<any> {
		const token = await this.getAuthToken();
		const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
		const init: RequestInit = { method, headers };
		if (body !== undefined) {
			headers['Content-Type'] = 'application/json';
			init.body = JSON.stringify(body);
		}
		const res = await fetch(`${this.baseUrl}${path}`, init);
		if (!res.ok) {
			throw new Error(`${res.status} ${res.statusText}`);
		}
		const contentType = res.headers.get('content-type') || '';
		if (contentType.includes('text/plain')) {
			return res.text();
		}
		return res.json();
	}

	public accountName(accountId: string): string {
		const acc = mwx.allAccounts.find((a) => a.accountId === accountId);
		return acc ? acc.companyName : accountId;
	}

	public get currentDurationMode() {
		return this.durationModes[this.durationMode];
	}

	public cycleDurationMode() {
		this.durationMode = (this.durationMode + 1) % this.durationModes.length;
	}

	public get memBarVariant(): string {
		if (!this.systemData) { return 'bg-success'; }
		const pct = parseFloat(this.systemData.os.usagePercent);
		if (pct > 90) { return 'bg-danger'; }
		if (pct > 70) { return 'bg-warning'; }
		return 'bg-success';
	}

	public async refreshAll() {
		this.loading = true;
		await Promise.all([
			this.fetchSystem(),
			this.fetchJobs(),
			this.fetchFiles(),
			this.fetchLogs(),
			this.fetchParquetConfig(),
		]);
		this.loading = false;
	}

	public async fetchSystem() {
		try {
			this.systemData = await this.apiFetch('/debug/system');
		} catch {
			this.systemData = null;
		}
	}

	public async fetchJobs() {
		this.jobsError = '';
		try {
			const data = await this.apiFetch('/debug/jobs');
			this.poolStats = data.pools || null;
			this.jobSummary = {
				active: data.active,
				pending: data.pending,
				completed: data.completed,
				failed: data.failed,
				cancelled: data.cancelled,
			};
			this.jobsList = data.jobs || [];

			// Fetch details for processing jobs to build active file map
			const processingJobs = this.jobsList.filter((j) => j.status === 'processing');
			const fileMap = new Map<string, any>();
			await Promise.all(processingJobs.map(async (job) => {
				try {
					const detail = await this.apiFetch(`/debug/jobs/${job.jobId}`);
					if (detail.csvFiles) {
						for (const csv of detail.csvFiles) {
							const basename = csv.localPath ? csv.localPath.split('/').pop() : null;
							if (basename) {
								fileMap.set(basename, {
									uploading: csv.uploading,
									converting: csv.converting,
									parquetUploading: csv.parquetUploading,
								});
							}
						}
					}
				} catch {
					// ignore individual job detail failures
				}
			}));
			this.activeFileMap = fileMap;
		} catch (e: any) {
			this.jobsError = `Failed to fetch jobs: ${e.message}`;
			this.jobSummary = null;
			this.jobsList = [];
			this.poolStats = null;
			this.activeFileMap = new Map();
		}
	}

	public async fetchFiles() {
		this.filesError = '';
		try {
			this.filesData = await this.apiFetch('/debug/files');
		} catch (e: any) {
			this.filesError = `Failed to fetch files: ${e.message}`;
			this.filesData = null;
		}
	}

	public async fetchLogs() {
		this.logsError = '';
		try {
			const data = await this.apiFetch('/debug/logs');
			this.logs = Array.isArray(data) ? data.join('\n') : String(data);
		} catch (e: any) {
			this.logsError = `Failed to fetch logs: ${e.message}`;
			this.logs = '';
		}
	}

	public async fetchParquetConfig() {
		this.parquetConfigError = '';
		try {
			const data = await this.apiFetch('/debug/parquet-config');
			this.parquetAllowAll = data.allowAll;
			this.parquetAccountsInput = (data.accounts || []).join(', ');
			this.parquetBudgetMB = data.budgetMB || 150;
			this.maxExtracts = data.maxExtracts || 10;
			this.maxUploads = data.maxUploads || 20;
		} catch (e: any) {
			this.parquetConfigError = `Failed to fetch parquet config: ${e.message}`;
		}
	}

	public async saveParquetConfig() {
		this.parquetConfigError = '';
		try {
			const accounts = this.parquetAccountsInput
				.split(',')
				.map((s: string) => s.trim())
				.filter((s: string) => s.length > 0);
			await this.apiFetch('/debug/parquet-config', 'PUT', {
				accounts,
				allowAll: this.parquetAllowAll,
				budgetMB: this.parquetBudgetMB,
				maxExtracts: this.maxExtracts,
				maxUploads: this.maxUploads,
			});
		} catch (e: any) {
			this.parquetConfigError = `Failed to save parquet config: ${e.message}`;
		}
	}

	public async retryJob(jobId: string) {
		try {
			await this.apiFetch(`/debug/jobs/${jobId}/retry`, 'POST');
			await this.fetchJobs();
		} catch (e: any) {
			this.jobsError = `Retry failed: ${e.message}`;
		}
	}

	public fileActivity(filename: string): Array<{ label: string; variant: string }> {
		const flags = this.activeFileMap.get(filename);
		if (!flags) { return []; }
		const badges: Array<{ label: string; variant: string }> = [];
		if (flags.uploading) { badges.push({ label: 'uploading', variant: 'primary' }); }
		if (flags.converting) { badges.push({ label: 'converting', variant: 'warning' }); }
		if (flags.parquetUploading) { badges.push({ label: 'pqt uploading', variant: 'info' }); }
		return badges;
	}

	public csvTotal(progress: any): number {
		return progress.totalCsv != null ? progress.totalCsv : progress.total;
	}

	public pqtTotal(progress: any): number {
		return progress.totalParquet != null ? progress.totalParquet : progress.total;
	}

	public statusVariant(status: string): string {
		switch (status) {
			case 'processing': return 'primary';
			case 'pending': return 'warning';
			case 'completed': return 'success';
			case 'failed': return 'danger';
			case 'cancelled': return 'secondary';
			default: return 'light';
		}
	}

	public totalDuration(d: any): string {
		let total = 0;
		for (const key of ['queue', 'extract', 'upload', 'parquetConvert', 'parquetUpload', 'firestore']) {
			if (d[key] != null) { total += d[key]; }
		}
		return total > 0 ? this.formatMs(total) : '-';
	}

	public formatMs(ms: number): string {
		if (ms < 1000) { return `${ms}ms`; }
		if (ms < 60000) { return `${(ms / 1000).toFixed(1)}s`; }
		const m = Math.floor(ms / 60000);
		const s = Math.round((ms % 60000) / 1000);
		return `${m}m${s}s`;
	}

	public formatUptime(seconds: number): string {
		const d = Math.floor(seconds / 86400);
		const h = Math.floor((seconds % 86400) / 3600);
		const m = Math.floor((seconds % 3600) / 60);
		if (d > 0) { return `${d}d ${h}h`; }
		if (h > 0) { return `${h}h ${m}m`; }
		return `${m}m`;
	}

	public formatAge(timestamp: number): string {
		const minutes = Math.round((Date.now() - timestamp) / 60000);
		if (minutes < 60) { return `${minutes}m ago`; }
		const hours = Math.round(minutes / 60);
		if (hours < 24) { return `${hours}h ago`; }
		return `${Math.round(hours / 24)}d ago`;
	}
}
